import type { ReactNode } from 'react';

/**
 * Drag payloads of the catalog. A board tile and a database card (`board-tile` / `database-tile`,
 * set by their own components) and a section row (`section-row`) are told apart by `type` only, so
 * the section tree can accept resources without knowing their components.
 */
export interface DraggedResource {
  kind: 'board' | 'database' | 'document';
  id: string;
  workspaceId: string;
}

export const databaseTileData = (workspaceId: string, databaseId: string) => ({
  type: 'database-tile',
  workspaceId,
  databaseId,
});

export const documentTileData = (workspaceId: string, documentId: string) => ({
  type: 'document-tile',
  workspaceId,
  documentId,
});

export const groupCardData = (workspaceId: string, groupId: string) => ({
  type: 'group-card',
  workspaceId,
  groupId,
});

export const sectionRowData = (workspaceId: string, sectionId: string) => ({
  type: 'section-row',
  workspaceId,
  sectionId,
});

type Data = Record<string | symbol, unknown>;

export function resourceOf(data: Data): DraggedResource | null {
  if (typeof data.workspaceId !== 'string') return null;
  if (data.type === 'board-tile' && typeof data.boardId === 'string') {
    return { kind: 'board', id: data.boardId, workspaceId: data.workspaceId };
  }
  if (data.type === 'database-tile' && typeof data.databaseId === 'string') {
    return { kind: 'database', id: data.databaseId, workspaceId: data.workspaceId };
  }
  if (data.type === 'document-tile' && typeof data.documentId === 'string') {
    return { kind: 'document', id: data.documentId, workspaceId: data.workspaceId };
  }
  return null;
}

export function sectionOf(data: Data): { id: string; workspaceId: string } | null {
  return data.type === 'section-row' &&
    typeof data.sectionId === 'string' &&
    typeof data.workspaceId === 'string'
    ? { id: data.sectionId, workspaceId: data.workspaceId }
    : null;
}

export function groupOf(data: Data): { id: string; workspaceId: string } | null {
  return data.type === 'group-card' &&
    typeof data.groupId === 'string' &&
    typeof data.workspaceId === 'string'
    ? { id: data.groupId, workspaceId: data.workspaceId }
    : null;
}

/** One extra row of a catalog card's menu (its actions do not depend on the card's own feature). */
export interface CardMenuAction {
  id: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
}
